import React from 'react';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useTheme } from '../../hooks/useTheme';
import { THEME_COLORS } from '../../constants/theme';
import { getHotelDateFormat } from '../../../utils/dateUtils';

/**
 * Reusable DatePicker Component
 * 
 * Provides consistent theming and styling for date pickers.
 * Automatically adapts to light/dark theme.
 * 
 * @component
 * @example
 * <FormDatePicker
 *   label="Start Date"
 *   value={startDate}
 *   onChange={(newValue) => setStartDate(newValue)}
 * />
 */
const FormDatePicker = ({ 
  value,
  onChange,
  label,
  size = 'small',
  fullWidth = true,
  sx = {},
  format,
  ...props 
}) => {
  const { isDark } = useTheme();
  const displayFormat = format || getHotelDateFormat();

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MuiDatePicker
        value={value}
        onChange={onChange}
        format={displayFormat}
        slotProps={{
          textField: {
            size,
            fullWidth,
            variant: 'outlined',
            sx: {
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: THEME_COLORS.PRIMARY,
                },
                '&:hover fieldset': {
                  borderColor: THEME_COLORS.PRIMARY,
                },
                '&.Mui-focused fieldset': {
                  borderColor: THEME_COLORS.PRIMARY,
                },
                '& input': {
                  color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                },
              },
              '& .MuiInputLabel-root': {
                color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_SECONDARY_LIGHT,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: THEME_COLORS.PRIMARY,
              },
              ...sx,
            },
          },
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

export default FormDatePicker;

